import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Save, X, Calendar, Package, MapPin, DollarSign, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { orderService, brandSpecService } from '../services/api';
import { useToast } from '../hooks/use-toast';
import UserNotesSystem from './UserNotesSystem';
import SearchableBrandDropdown from './SearchableBrandDropdown';

const AddOrder = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [brandOptions, setBrandOptions] = useState([]);
  
  // Order number will be auto-generated by the backend
  const [autoOrderNumber] = useState('Auto-generated');
  const [formData, setFormData] = useState({
    orderDate: new Date().toISOString().split('T')[0],
    brandName: '',
    brandId: null,
    poNumber: '',
    billTo: '',
    shipTo: '',
    quantity: '',
    unit: 'Nos',
    price: '',
    orderNotes: []
  });
  const [errors, setErrors] = useState({});

  // Load brand names from database
  useEffect(() => {
    const loadBrands = async () => {
      try {
        const brandNames = await brandSpecService.getBrandNames();
        setBrandOptions(brandNames);
      } catch (error) {
        console.error('Error loading brand names:', error);
        setBrandOptions([]);
      }
    };
    loadBrands();
  }, []);

  // Memoized handler for notes changes
  const handleNotesChange = useCallback((notes) => {
    setFormData(prev => ({ ...prev, orderNotes: notes }));
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleBrandSelection = (brand) => {
    if (brand) {
      setFormData(prev => ({ 
        ...prev, 
        brandName: brand.value || brand.label,
        brandId: brand.id
      }));
    } else {
      setFormData(prev => ({ 
        ...prev, 
        brandName: '',
        brandId: null
      }));
    }
    
    // Clear error when user selects a brand
    if (errors.brandName) {
      setErrors(prev => ({ ...prev, brandName: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.orderDate) newErrors.orderDate = 'Order date is required';
    if (!formData.brandName.trim()) newErrors.brandName = 'Brand name is required';
    
    const quantity = parseFloat(formData.quantity);
    if (!formData.quantity || isNaN(quantity) || quantity <= 0) {
      newErrors.quantity = 'Valid quantity is required';
    }
    
    const price = parseFloat(formData.price);
    if (!formData.price || isNaN(price) || price <= 0) {
      newErrors.price = 'Valid price is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSameBillToShipTo = () => {
    setFormData(prev => ({ ...prev, shipTo: prev.billTo }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields correctly.',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const orderData = {
        ...formData,
        quantity: parseFloat(formData.quantity),
        price: parseFloat(formData.price),
        dispatches: [],
        balance: parseFloat(formData.quantity),
        status: 'Pending',
        orderNotes: formData.orderNotes || [],
        createdBy: 'Admin User', // Simplified for dual-mode system
        createdDate: new Date().toISOString()
      };

      const newOrder = await orderService.createOrder(orderData);
      
      toast({
        title: 'Order Created',
        description: `Order ${newOrder.orderNumber} created successfully!`,
      });

      navigate('/orders');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create order. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All unsaved changes will be lost.')) {
      navigate('/orders');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Add New Order</h1>
          <p className="text-gray-600">Create a new order entry with all required details</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Calendar className="w-5 h-5 mr-2" />
                  Basic Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Auto-generated Order Number */}
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm font-medium text-green-900">Auto-Generated Order Number</Label>
                      <p className="text-lg font-bold text-green-700">{autoOrderNumber}</p>
                    </div>
                    <div className="text-xs text-green-600">
                      Unique identifier for this order
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="orderDate">Order Date *</Label>
                    <Input
                      id="orderDate"
                      type="date"
                      value={formData.orderDate}
                      onChange={(e) => handleInputChange('orderDate', e.target.value)}
                      className={errors.orderDate ? 'border-red-500' : ''}
                    />
                    {errors.orderDate && (
                      <p className="text-sm text-red-600">{errors.orderDate}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="poNumber">PO Number</Label>
                    <Input
                      id="poNumber"
                      type="text"
                      placeholder="Enter PO number (optional)"
                      value={formData.poNumber}
                      onChange={(e) => handleInputChange('poNumber', e.target.value)}
                    />
                  </div>
                </div>
                
                <SearchableBrandDropdown
                  brands={brandOptions}
                  value={formData.brandName}
                  onChange={handleBrandSelection}
                  placeholder="Type to search brands (e.g., 'Agri' for AgriGold)"
                  label="Brand Name"
                  required={true}
                  error={errors.brandName}
                />
              </CardContent>
            </Card>

            {/* Addresses */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  Addresses (Optional)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="billTo">Bill To Address</Label>
                  <Textarea
                    id="billTo"
                    placeholder="Complete billing address with city, state, pincode (optional)"
                    value={formData.billTo}
                    onChange={(e) => handleInputChange('billTo', e.target.value)}
                    rows={3}
                  />
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="shipTo">Ship To Address</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleSameBillToShipTo}
                      disabled={!formData.billTo}
                    >
                      Same as Bill To
                    </Button>
                  </div>
                  <Textarea
                    id="shipTo"
                    placeholder="Complete shipping address with city, state, pincode (optional)"
                    value={formData.shipTo}
                    onChange={(e) => handleInputChange('shipTo', e.target.value)}
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Quantity */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Package className="w-5 h-5 mr-2" />
                  Quantity Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="quantity">Quantity *</Label>
                    <Input
                      id="quantity"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="Enter quantity"
                      value={formData.quantity}
                      onChange={(e) => handleInputChange('quantity', e.target.value)}
                      className={errors.quantity ? 'border-red-500' : ''}
                    />
                    {errors.quantity && (
                      <p className="text-sm text-red-600">{errors.quantity}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="unit">Unit *</Label>
                    <Select value={formData.unit} onValueChange={(value) => handleInputChange('unit', value)}>
                      <SelectTrigger className={errors.unit ? 'border-red-500' : ''}>
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Nos">Nos (Numbers)</SelectItem>
                        <SelectItem value="Kgs">Kgs (Kilograms)</SelectItem>
                        <SelectItem value="Tons">Tons</SelectItem>
                        <SelectItem value="Meters">Meters</SelectItem>
                        <SelectItem value="Pieces">Pieces</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.unit && (
                      <p className="text-sm text-red-600">{errors.unit}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pricing */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <DollarSign className="w-5 h-5 mr-2" />
                  Pricing
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label htmlFor="price">Price per unit *</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="Enter price per unit"
                      value={formData.price}
                      onChange={(e) => handleInputChange('price', e.target.value)}
                      className={`pl-8 ${errors.price ? 'border-red-500' : ''}`}
                    />
                  </div>
                  {errors.price && (
                    <p className="text-sm text-red-600">{errors.price}</p>
                  )}
                  {formData.quantity && formData.price && (
                    <p className="text-sm text-gray-600">
                      Total order value: ₹{(parseFloat(formData.price) * parseFloat(formData.quantity) || 0).toFixed(2)}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Order Notes */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FileText className="w-5 h-5 mr-2" />
                  Order Notes
                </CardTitle>
                <p className="text-sm text-gray-600">Add notes and comments for this order</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <UserNotesSystem 
                  brandId="new-order-form"
                  onNotesChange={handleNotesChange}
                  initialNotes={formData.orderNotes || []}
                />
              </CardContent>
            </Card>

            {/* Submit Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6">
              <Button
                type="submit"
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating Order...
                  </div>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Create Order
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={loading}
                className="flex-1"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddOrder;